import { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowUpDown } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import PaymentStatusBadge from "@/components/booking/PaymentStatusBadge";
import type { BookingWithCourt, BookingPaymentRow } from "@/hooks/useMyBookings";
import { formatPrice } from "@/utils/formatPrice";
import {
  resolveBookingDisplayAmount,
  bookingDisplayAmountMinor,
} from "@/lib/bookingDisplayAmount";

type SortKey = "date" | "time" | "court" | "amount" | "status";
type SortDir = "asc" | "desc";

interface BookingHistoryTableProps {
  bookings: BookingWithCourt[];
  payments: BookingPaymentRow[];
  userId: string | undefined;
  onSelect: (bookingId: string) => void;
}

export default function BookingHistoryTable({
  bookings,
  payments,
  userId,
  onSelect,
}: BookingHistoryTableProps) {
  const { t } = useTranslation("booking");
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const getPaymentStatus = (b: BookingWithCourt) => {
    const bp = payments.filter((p) => p.booking_id === b.id);
    const up = bp.find((p) => p.user_id === userId);
    // Sibling-badge fix: paid group siblings have no own payment row — fall back
    // to the booking's own payment_status, not hardcoded "unpaid".
    return up?.status ?? (bp.length > 0 ? bp[0].status : (b.payment_status ?? "unpaid"));
  };

  const getPlayerCount = (b: BookingWithCourt) => {
    return payments.filter((p) => p.booking_id === b.id).length || 1;
  };

  const filtered = useMemo(() => {
    let result = [...bookings];

    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result.filter((b) => new Date(b.start_time) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo + "T23:59:59");
      result = result.filter((b) => new Date(b.start_time) <= to);
    }

    result.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "date":
        case "time":
          cmp = new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
          break;
        case "court":
          cmp = (a.courts?.name ?? "").localeCompare(b.courts?.name ?? "");
          break;
        case "amount":
          // Samma källa som kolumnen renderar — annars sorterar den på ett
          // annat värde än användaren ser.
          cmp =
            bookingDisplayAmountMinor(a, payments, userId, bookings) -
            bookingDisplayAmountMinor(b, payments, userId, bookings);
          break;
        case "status":
          cmp = getPaymentStatus(a).localeCompare(getPaymentStatus(b));
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });

    return result;
  }, [bookings, sortKey, sortDir, dateFrom, dateTo, payments, userId]);

  const SortHeader = ({ label, sortKeyName }: { label: string; sortKeyName: SortKey }) => (
    <Button
      variant="ghost"
      size="sm"
      className="h-auto p-0 font-medium text-muted-foreground hover:text-foreground"
      onClick={() => handleSort(sortKeyName)}
    >
      {label}
      <ArrowUpDown className="ml-1 h-3 w-3" />
    </Button>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground">{t("from")}:</label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-8 w-36 text-xs"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground">{t("to")}:</label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-8 w-36 text-xs"
          />
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead><SortHeader label={t("date")} sortKeyName="date" /></TableHead>
            <TableHead><SortHeader label={t("time")} sortKeyName="time" /></TableHead>
            <TableHead><SortHeader label={t("court")} sortKeyName="court" /></TableHead>
            <TableHead>{t("players")}</TableHead>
            <TableHead><SortHeader label={t("amount")} sortKeyName="amount" /></TableHead>
            <TableHead><SortHeader label={t("status")} sortKeyName="status" /></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((b) => (
            <TableRow
              key={b.id}
              className="cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => onSelect(b.id)}
            >
              <TableCell className="text-sm font-medium">
                {format(new Date(b.start_time), "EEE d MMM", { locale: sv })}
              </TableCell>
              <TableCell className="text-sm">
                {format(new Date(b.start_time), "HH:mm")}–
                {format(new Date(b.end_time), "HH:mm")}
              </TableCell>
              <TableCell className="text-sm">{b.courts?.name}</TableCell>
              <TableCell className="text-sm">{getPlayerCount(b)}</TableCell>
              <TableCell className="text-sm">
                {(() => {
                  const amount = resolveBookingDisplayAmount(b, payments, userId, bookings);
                  if (amount.kind === "groupSibling") {
                    return (
                      <span className="text-xs text-muted-foreground">
                        {t("partOfGroupBooking")}
                      </span>
                    );
                  }
                  if (amount.kind === "none") return "–";
                  return (
                    <>
                      {formatPrice(amount.money)}
                      {amount.note && (
                        <span className="ml-1 text-xs text-muted-foreground">({amount.note})</span>
                      )}
                    </>
                  );
                })()}
              </TableCell>
              <TableCell>
                <PaymentStatusBadge status={getPaymentStatus(b)} />
              </TableCell>
            </TableRow>
          ))}
          {filtered.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                {t("noHistory")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
